import React, { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";

import { COUNTRY_NAMES, Constants } from "../../../@types/Constants";
import { IUpdateMemberProfile } from "../../../@types/IUpdateMemberProfile";
import { IUserProfileForm } from "../../../@types/IUserProfileForm";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  clearUpdateRequestStatus,
  setCRMProfile,
  setCRMBasicProfile,
  updateRequestStatus,
} from "../../../redux/slices/Member/member";
import { memberServiceProvider } from "../../../services/ServiceProvider";
import { regions } from "../../../services/address";
import { getToken } from "../../../utils/auth0Client";
import ErrorMessageAlert from "../../common/ErrorMessageAlert/ErrorMessageAlert";

const ProfileEditForm: React.FC<any> = (props) => {
  const [isCoutryUsCanadaSelected, setIsCoutryUsCanadaSelected] =
    useState(true);
  const isMountedRef = useRef<HTMLDivElement | null | boolean>(null);
  const dispatch = useAppDispatch();
  const showEditForm = props.setShowEditForm;

  const { register, handleSubmit, errors, watch, setValue } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
  });
  const profile = useAppSelector((state: any) => {
    const member = state.member;
    return member && member.isLoggedIn ? member.crmBasicProfile : null;
  });

  const [isSaving, setIsSaving] = useState(false);
  const editRequestStatus = useAppSelector((state) => {
    const reqStatus = state.member.updateRequestStatus;
    return reqStatus && reqStatus.type === "edit-profile"
      ? reqStatus.status
      : null;
  });

  const saveProfile = async (
    data: IUserProfileForm,
    event?: React.BaseSyntheticEvent
  ) => {
    /**
     * Transforms form data into a request body that the API accepts. Ideally
     * the form data is structured in the same way as the API request so that
     * this type conversion is unnecessary.
     *
     * @todo: Change form data format.
     */
    const formDataToRequestBody = (
      formData: IUserProfileForm
    ): IUpdateMemberProfile => {
      return {
        email: formData.email,
        title: formData.title,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        address: {
          addressLine1: formData.addressLine1,
          city: formData.city,
          postalCode: formData.postalCode || "",
          stateCode: formData.stateCode || "",
          countryCode: formData.countryCode,
        },
        birthday: {
          month: formData.month,
          day: formData.day,
        },
        phones: {
          work: formData.work,
          mobile: formData.mobile,
          home: formData.home,
        },
        preferences: {
          eliteAmenity: formData.eliteAmenity,
          bedType: formData.bedType,
          roomLocation: formData.roomLocation ?? [],
          smokingRoom: formData.smokingRoom,
        },
      };
    };
    setIsSaving(true);
    const tokenId = getToken();
    const requestData = formDataToRequestBody(data);
    let response;

    // if (process.env.GATSBY_IS_CDP_ENABLED !== "ENABLE") {
    //   response = await updateMemberProfile(tokenId, requestData);
    // } else {
    //   // response = await completeSignUpProcess(requestData);
    //   response = await updateMemberProfileCDP(tokenId, requestData);
    // }
    try {
      const memberService = await memberServiceProvider();
      const response = await memberService.editUserProfile(requestData);
      await dispatch(setCRMProfile({ ...profile, ...requestData }));
      await dispatch(setCRMBasicProfile({ ...profile, ...requestData }));
      dispatch(
        updateRequestStatus({
          type: "edit-profile",
          status: "success",
        })
      );
    } catch (error) {
      dispatch(
        updateRequestStatus({
          type: "edit-profile",
          status: "error",
        })
      );
      console.error(error);
      throw error;
    }

    event?.preventDefault();

    isMountedRef.current && setIsSaving(false);
    return false;
  };

  const statesUS = Object.entries(regions["US"]).map(([stateKey, stateObj]) => {
    return (
      <option value={stateKey} key={stateKey}>
        {stateObj.name}
      </option>
    );
  });

  const statesCA = Object.entries(regions["CA"]).map(([stateKey, stateObj]) => {
    return (
      <option value={stateKey} key={stateKey}>
        {stateObj.name}
      </option>
    );
  });

  const closeEditForm = () => {
    isMountedRef.current && showEditForm(false);
  };

  useEffect(() => {
    if (profile) {
      if (profile.preferences?.bedType) {
        setValue("bedType", profile.preferences.bedType);
      }
      if (profile.preferences?.eliteAmenity) {
        setValue("eliteAmenity", profile.preferences.eliteAmenity);
      }
      if (profile.preferences?.roomLocation) {
        setValue("roomLocation", profile.preferences.roomLocation);
      }
      if (profile.preferences?.smokingRoom) {
        setValue("smokingRoom", profile.preferences.smokingRoom);
      }
      if (profile.title) {
        setValue("title", profile.title);
      }
      if (profile.firstName) {
        setValue("firstName", profile.firstName);
      }
      if (profile.lastName) {
        setValue("lastName", profile.lastName);
      }
      if (profile.birthday?.month) {
        setValue("month", profile.birthday.month);
      }
      if (profile.birthday?.day) {
        setValue("day", profile.birthday.day);
      }
      if (profile.gender) {
        setValue("gender", profile.gender);
      }
      if (profile.address?.countryCode) {
        setValue("countryCode", profile.address.countryCode);
      } else if (profile.address?.country) {
        setValue("countryCode", profile.address.country);
      }
      if (profile.address?.addressLine1) {
        setValue("addressLine1", profile.address.addressLine1);
      } else if (profile.address?.streetAddress1) {
        setValue("addressLine1", profile.address.streetAddress1);
      }

      if (profile.address?.city) {
        setValue("city", profile.address.city);
      } else if (profile.address?.locality) {
        setValue("city", profile.address.locality);
      }
      if (profile.address?.stateCode) {
        setValue("stateCode", profile.address.stateCode);
      } else if (profile.address?.region) {
        setValue("stateCode", profile.address.region);
      }
      if (profile.address?.postalCode) {
        setValue("postalCode", profile.address.postalCode);
      }
      if (profile.phones?.home) {
        setValue("home", profile.phones.home);
      }
      if (profile.phones?.mobile) {
        setValue("mobile", profile.phones.mobile);
      }
      if (profile.phones?.work) {
        setValue("work", profile.phones.work);
      }
      if (profile.email) {
        setValue("email", profile.email);
      }
    }
  }, [profile, setValue]);

  useEffect(() => {
    isMountedRef.current = true;
    editRequestStatus === "success" && closeEditForm();

    return () => {
      isMountedRef.current = false;
    };
  }, [editRequestStatus]);

  useEffect(() => {
    return () => {
      dispatch(clearUpdateRequestStatus());
    };
  }, []);

  const stateLabel = watch("country_code") == "CA" ? "Province" : "State";
  const smokingRoom = ["Smoking", "Non Smoking"];
  const roomLocation = [
    "No Preference",
    "Near Elevator",
    "Away from Elevator",
    "Low Floor",
    "High Floor",
  ];
  const bedType = ["No Preference", "One Bed", "Two Bed"];
  const eliteAmenity = [
    "No Preference",
    "Water",
    "Fruit",
    "Sweets",
    "Uniquely Local Gift",
  ];
  const day = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
    "11",
    "12",
    "13",
    "14",
    "15",
    "16",
    "17",
    "18",
    "19",
    "20",
    "21",
    "22",
    "23",
    "24",
    "25",
    "26",
    "27",
    "28",
    "29",
    "30",
    "31",
  ];
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    const subscription = watch((value, { name, type }) =>
      // eslint-disable-next-line no-console
      console.info(value, name, type)
    );
    const watchCountryCode = watch("countryCode");
    setIsCoutryUsCanadaSelected(
      watchCountryCode === "US" || watchCountryCode === "CA"
    );
  }, [watch]);
  React.useEffect(() => {
    if (!isCoutryUsCanadaSelected) {
      setValue("stateCode", "");
      setValue("postalCode", "");
    }
  }, [isCoutryUsCanadaSelected]);
  return (
    <>
      {profile && (
        <>
          {editRequestStatus === "error" && (
            <ErrorMessageAlert
              errorType={Constants.ERRORSTYPE.TRAVELPASS}
              errorSubType={Constants.ERRORSSUBTYPE.TRAVELPASS.UPDATEPROFILE}
              message={`An error occurred while updating profile details`}
            >
              <Alert variant={`danger`}>
                An error occurred updating profile details. Please try again.
              </Alert>
            </ErrorMessageAlert>
          )}
          <Form onSubmit={handleSubmit<IUserProfileForm>(saveProfile)}>
            <Form.Row>
              <Form.Group as={Col} controlId={`title`}>
                <Form.Label>Title</Form.Label>
                <Form.Control as="select" name={`title`} ref={register}>
                  <option value="">--Select Title-</option>
                  <option value="Mr">Mr.</option>
                  <option value="Mrs">Mrs.</option>
                  <option value="Ms">Ms.</option>
                  <option value="Dr">Dr.</option>
                </Form.Control>
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`firstName`}>
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type={`text`}
                  name={`firstName`}
                  placeholder={`First Name`}
                  ref={register}
                />
              </Form.Group>
              <Form.Group as={Col} controlId={`lastName`}>
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type={`text`}
                  name={`lastName`}
                  placeholder={`Last Name`}
                  ref={register}
                />
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`birthday.month`}>
                <Form.Label>Birthday Month</Form.Label>
                <Form.Control as="select" name={`month`} ref={register}>
                  <option value="">--Select Month--</option>
                  <option value="1">Jan</option>
                  <option value="2">Feb</option>
                  <option value="3">Mar</option>
                  <option value="4">Apr</option>
                  <option value="5">May</option>
                  <option value="6">Jun</option>
                  <option value="7">Jul</option>
                  <option value="8">Aug</option>
                  <option value="9">Sep</option>
                  <option value="10">Oct</option>
                  <option value="11">Nov</option>
                  <option value="12">Dec</option>
                </Form.Control>
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`birthday.day`}>
                <Form.Label>Birthday date</Form.Label>
                <Form.Control as="select" name={`day`} ref={register}>
                  <option value="">--Select Date--</option>
                  {day.map((d: string) => (
                    <option key={d} value={d}>
                      {d}{" "}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`gender`}>
                <Form.Label>Gender</Form.Label>
                <Form.Control as="select" name={`gender`} ref={register}>
                  <option value="">--Select Gender--</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </Form.Control>
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`address.countryCode`}>
                <Form.Label>Country</Form.Label>
                <Form.Control as="select" name={`countryCode`} ref={register}>
                  {Object.entries(COUNTRY_NAMES)?.map((arr) => (
                    <>
                      <option value={arr[0]} key={arr[0]}>
                        {arr[1]}
                      </option>
                      {arr[0] === "CA" && <hr />}
                    </>
                  ))}
                </Form.Control>
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`address.addressLine1`}>
                <Form.Label>Address </Form.Label>
                <Form.Control
                  type="text"
                  name="addressLine1"
                  ref={register}
                  isInvalid={errors.addressLine1}
                  aria-invalid={errors.address ? "true" : "false"}
                  autoComplete="street-address"
                />
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`address.city`}>
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  name="city"
                  ref={register}
                  isInvalid={errors.city}
                  aria-invalid={errors.city ? "true" : "false"}
                  maxLength={50}
                  autoComplete="address-level2"
                />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`address.stateCode`}>
                <Form.Label>{stateLabel}</Form.Label>
                <Form.Control
                  as="select"
                  name="stateCode"
                  ref={register}
                  isInvalid={errors.stateCode}
                  autoComplete="address-level1"
                  disabled={!isCoutryUsCanadaSelected}
                >
                  <option value="">--Select State--</option>
                  <optgroup label="United States">{statesUS}</optgroup>
                  <optgroup label="Canada">{statesCA}</optgroup>
                </Form.Control>
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`address.postalCode`}>
                <Form.Label>Zip/Postal Code</Form.Label>
                <Form.Control
                  type="text"
                  name="postalCode"
                  ref={register}
                  isInvalid={errors.postalCode}
                  maxLength={20}
                  aria-invalid={errors.postalCode ? "true" : "false"}
                  autoComplete="postal-code"
                  disabled={!isCoutryUsCanadaSelected}
                />
              </Form.Group>
            </Form.Row>

            <Form.Row>
              <Form.Group as={Col} controlId={`phones.home`}>
                <Form.Label>Home Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="home"
                  ref={register}
                  isInvalid={errors.home}
                  maxLength={15}
                  aria-invalid={errors.home ? "true" : "false"}
                  autoComplete="tel"
                />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`phones.mobile`}>
                <Form.Label>Mobile Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="mobile"
                  ref={register}
                  isInvalid={errors.mobile}
                  maxLength={15}
                  aria-invalid={errors.mobile ? "true" : "false"}
                  autoComplete="tel"
                />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`phones.work`}>
                <Form.Label>Business Phone</Form.Label>
                <Form.Control
                  type="text"
                  name="work"
                  ref={register}
                  isInvalid={errors.work}
                  maxLength={15}
                  aria-invalid={errors.work ? "true" : "false"}
                  autoComplete="tel"
                />
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <Form.Group as={Col} controlId={`email`}>
                <Form.Label className="read-only">Email</Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  placeholder={`Email`}
                  ref={register}
                  readOnly
                  autoComplete="email"
                />
              </Form.Group>
            </Form.Row>

            <Row className="pt-2">
              <Col className="text-center">
                <Button
                  type="submit"
                  variant="link"
                  size="sm"
                  disabled={isSaving}
                  id="save-changes-button"
                >
                  {isSaving ? "Saving..." : "SAVE CHANGES"}
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={closeEditForm}
                  disabled={isSaving}
                  id="cancel-changes-button"
                >
                  CANCEL CHANGES
                </Button>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </>
  );
};
export default ProfileEditForm;
